import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { Store } from '@ngrx/store';
import * as fromRootStore from '@store/index';
import { selectVgClientPoid } from '@store/selectors/client-accounts/client-accounts.selectors';
import { selectIsOrgToOrgTransfer } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { combineLatest, mergeMap, Observable, Subscription, take } from 'rxjs';

import { CookiesService } from '../../cookies/cookies';
import { WindowRef } from '../../window-ref/window-ref';
import { SKIP_URL_MUTATION } from '../context-tokens';

export const IS_ORG_TRANSFER_HEADER = 'Is-Org-Transfer';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private $window: any;
  subs: Subscription = new Subscription();
  constructor(
    private $cookies: CookiesService,
    private windowRef: WindowRef,
    private globalStore: Store<fromRootStore.State>,
  ) {
    this.$window = windowRef.nativeWindow;
  }

  /**
   * Local Development:
   * We need to prefix the http request URL with an absolute domain for local development
   * because the URLs in the application are all relative and get routed to middletier
   * via NGINX
   *
   * Internal Envs:
   * We need to prefix the http request URL with an absolute domain for internal environments,
   * all internal users will be on the vanguard network
   *
   * External Envs:
   * All URLs are relative / don't contain domain. The routing of traffic happens at the NGINX level,
   * and is differentiated by /api path prefix.
   */
  private isPublic(url: string): boolean {
    return !url.includes('secure') && !url.includes('vgim');
  }

  private getCwpPath(url: string, satEnv?: number): string {
    return `usa/api/cwp-change-of-ownership${satEnv ? `/${satEnv}` : ''}${this.isPublic(url) ? '/public' : ''}`;
  }

  private getURL(url: string): string {
    switch (environment.name) {
      // Local development
      case 'local.external':
      case 'local.internal':
        return `https://localhost.vanguard.com:8081/${url}`;
      case 'local.test.external':
        return `https://satpersonal1.vanguard.com/${this.getCwpPath(url)}/${url}`;
      case 'local.test-2.external':
      case 'test-2.external':
        return `https://satpersonal1.vanguard.com/${this.getCwpPath(url, 2)}/${url}`;
      case 'local.test-3.external':
      case 'test-3.external':
        return `https://satpersonal1.vanguard.com/${this.getCwpPath(url, 3)}/${url}`;
      case 'local.test-4.external':
      case 'test-4.external':
        return `https://satpersonal1.vanguard.com/${this.getCwpPath(url, 4)}/${url}`;
      // Internal environments
      case 'eng.internal':
      // eng evironment points to SAT for testing purposes
      case 'test.internal':
        return `https://idpi.retailgeneral2t.c1.vanguard.com/api/cwp-change-of-ownership/${this.isPublic(url) ? 'public/' : ''}${url}`;
      case 'prod.internal':
        return `https://idpi.retailgeneral2p.c1.vanguard.com/api/cwp-change-of-ownership/${this.isPublic(url) ? 'public/' : ''}${url}`;
      // External environments
      // eng evironment points to SAT for testing purposes
      case 'eng.external':
      case 'test.external':
        return `https://satpersonal1.vanguard.com/${this.getCwpPath(url)}/${url}`;
      case 'prod.external':
        return `https://personal1.vanguard.com/${this.getCwpPath(url)}/${url}`;
      // e2e environment - cy.intercepts don't have /api
      case 'e2e':
      case 'e2e-templates':
        return url;
      default:
        // Exhaustive check
        const n: never = environment.name;
        return url;
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return combineLatest([
      this.globalStore.select(flowTypeSelection),
      this.globalStore.select(selectVgClientPoid),
      this.globalStore.select(selectIsOrgToOrgTransfer),
    ]).pipe(
      take(1),
      mergeMap(([flowType, poid, isOrgToOrgTransfer]) => {
        /**
         * The XMLHttpRequest.withCredentials property is a Boolean that indicates
         * whether or not cross-site Access-Control requests should be made using
         * credentials such as cookies, authorization headers or TLS client certificates.
         * Setting withCredentials has no effect on same-site requests.
         */
        const url =
          request.url.startsWith('https') || request.context.get(SKIP_URL_MUTATION)
            ? request.url
            : this.getURL(request.url);
        request = request.clone({ withCredentials: true, url });
        const headers: { [key: string]: string } = {};
        // Pass flowType to all requests
        if (flowType) {
          headers['Flow-Type'] = flowType;
        }
        // Pass poid to all requests (helps with public API calls that don't have poid attached to headers via Siteminder)
        if (poid) {
          headers.poid = poid;
        }
        // Pass isOrgTransfer flag to all requests for org-to-org transfers
        if (isOrgToOrgTransfer) {
          headers[IS_ORG_TRANSFER_HEADER] = 'true';
        }

        /**
         * When the XSRF token is not present on request headers on any POST call,
         * read value from cookie and manually set header.  Most scenarios where
         * header is missing is on CORS requests.
         */
        const xsrfToken = this.$cookies.get('XSRF-TOKEN');
        if (request.method === 'POST' && !request.headers.has('X-XSRF-TOKEN') && xsrfToken) {
          headers['X-XSRF-TOKEN'] = xsrfToken;
        }
        request = request.clone({
          setHeaders: headers,
        });

        return next.handle(request);
      }),
    );
  }
}
